import type { TicketPriority, TicketSource, TicketState } from '@servwell/shared';
import { dash, type DataTableColumn } from '@/components/ui/data-table';
import { StatusBadge } from '../ui/Badge';
import { dayInTz } from '../../lib/format';
import {
  PRIORITY_TONE,
  TICKET_STATE_TONE,
  categoryLabel,
  dueLabel,
  overdueLabel,
  priorityLabel,
  sourceLabel,
  stateLabel,
} from './ticketView';
import type { TicketRow } from './types';

/**
 * The small pieces every ticket surface draws the same way: the List, the Board, the panel and
 * the job's card. No hooks, so a test can call them.
 */

export function TicketStatusBadge({ status }: { status: TicketState }) {
  return <StatusBadge tone={TICKET_STATE_TONE[status]}>{stateLabel(status)}</StatusBadge>;
}

/** Normal reads quietly; the others are a badge, Urgent in the danger tone. */
export function TicketPriorityBadge({ priority }: { priority: TicketPriority }) {
  return priority === 'normal' ? (
    <span className="text-muted-foreground">Normal</span>
  ) : (
    <StatusBadge tone={PRIORITY_TONE[priority]}>{priorityLabel(priority)}</StatusBadge>
  );
}

/**
 * The reply-due, as a promise reads: "Overdue · 2 h" in the danger token when it has passed
 * on a ticket that is not resolved, else when it is due, else nothing.
 */
export function ReplyDue({
  ticket,
  tz,
  now,
}: {
  ticket: { replyDueAt: Date | string | null; status: TicketState };
  tz: string;
  now: number;
}) {
  const late = overdueLabel(ticket, now);
  if (late) {
    return (
      <span data-overdue="true" className="font-medium text-[var(--sw-danger-text)]">
        {late}
      </span>
    );
  }
  const due = ticket.status === 'resolved' ? null : dueLabel(ticket.replyDueAt, tz, now);
  return due ? (
    <span className="text-muted-foreground">{due}</span>
  ) : (
    <span className="text-muted-foreground">—</span>
  );
}

/**
 * A customer's ticket is marked where tickets are listed (spec §5): it came from outside the
 * shop, and nobody in it has seen it yet. The office's and a tech's read as nothing.
 */
export function TicketSourceChip({ source }: { source: TicketSource }) {
  return source === 'customer' ? (
    <StatusBadge tone="info">{sourceLabel(source)}</StatusBadge>
  ) : null;
}

/** "#12 Dripping again": one name for a ticket on every surface. */
export const ticketName = (t: { number: number; title: string }) =>
  `#${t.number} ${t.title}`;

/**
 * A List row's or Board card's accessible name. It replaces the row's text for a screen
 * reader, so it says what the chip shows: a customer's ticket is "from a customer".
 */
export const ticketRowLabel = (t: {
  number: number;
  title: string;
  source: TicketSource;
}) => `Open ticket ${ticketName(t)}${t.source === 'customer' ? ', from a customer' : ''}`;

/**
 * The List's columns. Server mode sorts by each `key` through `TICKET_SORT` in
 * `services/ticket.ts`, so a sortable column (one with a `value`) needs its key there; the
 * owner is not sortable there, and has no `value`.
 */
export function ticketColumns(tz: string, now: number): DataTableColumn<TicketRow>[] {
  return [
    {
      key: 'number',
      label: 'Ticket',
      value: (t) => t.number,
      cell: (t) => (
        <div className="min-w-0 max-w-[22rem]">
          <p className="truncate font-medium">{t.title}</p>
          <p className="flex items-center gap-2">
            <span className="font-mono text-xs text-muted-foreground">#{t.number}</span>
            <TicketSourceChip source={t.source} />
          </p>
        </div>
      ),
    },
    {
      key: 'customer',
      label: 'Customer',
      value: (t) => t.customer.displayName,
      cell: (t) => (
        <div>
          <p>{t.customer.displayName}</p>
          {t.job ? (
            <p className="text-xs text-muted-foreground">Job #{t.job.jobNumber}</p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      value: (t) => t.status,
      cell: (t) => <TicketStatusBadge status={t.status} />,
    },
    {
      key: 'priority',
      label: 'Priority',
      value: (t) => t.priority,
      cell: (t) => <TicketPriorityBadge priority={t.priority} />,
    },
    {
      key: 'category',
      label: 'Category',
      value: (t) => t.category,
      cell: (t) => categoryLabel(t.category),
    },
    {
      key: 'owner',
      label: 'Owner',
      cell: (t) =>
        t.owner ? (
          dash(t.owner.name ?? 'Former staff')
        ) : (
          <span className="text-muted-foreground">Nobody yet</span>
        ),
    },
    {
      key: 'replyDue',
      label: 'Reply due',
      value: (t) => (t.replyDueAt ? new Date(t.replyDueAt).getTime() : null),
      searchValue: () => null,
      cell: (t) => <ReplyDue ticket={t} tz={tz} now={now} />,
    },
    {
      key: 'updated',
      label: 'Updated',
      value: (t) => new Date(t.updatedAt).getTime(),
      searchValue: () => null,
      cell: (t) => dayInTz(t.updatedAt, tz),
      align: 'right',
    },
  ];
}
